import { useState, type ChangeEvent } from 'react'

import { useMutation } from '@apollo/client'
import { Checkbox, Modal, TextInput } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { CREATE_CARRIER_CONTACT } from 'src/graphql/carriers.graphql'
import { CarrierCurationStatus } from 'src/graphql/types/carriers'

import {
  draftErrors,
  draftHasErrors,
  type ContactDraft,
} from './ContactRowFields'

export interface AddContactModalProps {
  carrierId: string
  opened: boolean
  onClose: () => void
  /**
   * One of CarrierCurationStatus. Only an UNREVIEWED carrier is offered the
   * review tick: the other rungs are already past it.
   */
  curationStatus?: string | null
  /** Called once the server has a row for the typing, new or already there. */
  onAdded: () => void
}

const EMPTY_DRAFT: ContactDraft = {
  contactName: '',
  email: '',
  phoneNumber: '',
}

export const EXISTED_MESSAGE =
  'That email or phone is already on file for this carrier; showing the existing contact.'

/**
 * Adds a contact an admin learned of directly — a working number from a call,
 * an address off a carrier's own letterhead.
 *
 * Validated against the same rules the in-row editor uses, so a value this
 * form accepts is one the table would have accepted too. The server checks
 * again and refuses a suppressed value or a malformed one with a reason; that
 * reason is shown and the typing kept, since the refusal is the one moment it
 * is worth keeping.
 */
const AddContactModal = ({
  carrierId,
  opened,
  onClose,
  curationStatus,
  onAdded,
}: AddContactModalProps) => {
  const [draft, setDraft] = useState<ContactDraft>(EMPTY_DRAFT)
  const [markReviewed, setMarkReviewed] = useState(true)

  // An admin who types in a number has usually just checked the row, so the
  // tick is offered and on by default — but only where it changes something.
  const offersReview = curationStatus === CarrierCurationStatus.UNREVIEWED

  const reset = () => {
    setDraft(EMPTY_DRAFT)
    setMarkReviewed(true)
  }

  const close = () => {
    reset()
    onClose()
  }

  const [createContact, { loading }] = useMutation(CREATE_CARRIER_CONTACT, {
    onCompleted: (result) => {
      const existed = Boolean(result?.createCarrierContact?.existed)
      // Toast has no info style, so the heads-up is drawn as a warning: the
      // admin asked for a new row and is being shown an old one instead.
      if (existed) toast(EXISTED_MESSAGE, 'warning')
      else toast('Contact added', 'success')
      reset()
      onAdded()
      onClose()
    },
    // Handled here so the promise resolves rather than rejecting. The modal
    // stays open with the values: the message says what to fix.
    onError: (error) => toast(error.message, 'error'),
  })

  const errors = draftErrors(draft)
  const hasContactPoint = Boolean(
    draft.email.trim() || draft.phoneNumber.trim()
  )
  const canSubmit = hasContactPoint && !draftHasErrors(draft) && !loading

  // Blank fields travel as absent rather than as empty strings, so the server
  // never has to decide what an empty email means.
  const optional = (value: string) => value.trim() || undefined

  const submit = () => {
    if (!canSubmit) return
    void createContact({
      variables: {
        carrierId,
        input: {
          contactName: optional(draft.contactName),
          email: optional(draft.email),
          phoneNumber: optional(draft.phoneNumber),
          // Sent only where the tick was shown; otherwise the server's default
          // stands and a REVIEWED or LOCKED carrier is left as it is.
          markReviewed: offersReview ? markReviewed : undefined,
        },
      },
    })
  }

  const setField =
    (field: keyof ContactDraft) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value
      setDraft((current) => ({ ...current, [field]: value }))
    }

  return (
    <Modal opened={opened} onClose={close} title="Add contact" centered>
      <form
        className="flex flex-col gap-4 text-sm"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <TextInput
          id="add-contact-name"
          label="Contact name"
          placeholder="e.g. Safety Department"
          value={draft.contactName}
          onChange={setField('contactName')}
          data-autofocus
        />
        <TextInput
          id="add-contact-email"
          label="Email"
          placeholder="verifications@carrier.com"
          value={draft.email}
          onChange={setField('email')}
          error={errors.email || undefined}
          classNames={{ input: 'font-mono' }}
        />
        <TextInput
          id="add-contact-phone"
          label="Phone"
          placeholder="(555) 123-4567"
          value={draft.phoneNumber}
          onChange={setField('phoneNumber')}
          error={errors.phoneNumber || undefined}
        />
        <div className="text-xs text-doubleNickel-gray-500">
          Enter at least an email address or a phone number.
        </div>

        {offersReview && (
          <Checkbox
            id="add-contact-mark-reviewed"
            label="Mark this carrier as reviewed"
            description="Automated sources will then only fill empty fields on this carrier."
            checked={markReviewed}
            onChange={(event) => setMarkReviewed(event.currentTarget.checked)}
          />
        )}

        <div className="flex justify-end gap-2">
          <DNButton variant="transparent" type="button" onClick={close}>
            Cancel
          </DNButton>
          <DNButton
            variant="primary"
            type="submit"
            loading={loading}
            disabled={!canSubmit}
          >
            Add contact
          </DNButton>
        </div>
      </form>
    </Modal>
  )
}

export default AddContactModal
